import { useState } from "react";
import {
  Phone,
  MessageCircle,
  Mail,
  Globe,
  MapPin,
  Send,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import { Leads } from "@/lib/api";
import { useRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";

const CONTACT = {
  phone1: "70169 75324",
  phone1Tel: "+917016975324",
  phone2: "+91 98796 30926",
  phone2Tel: "+919879630926",
  phone3: "02626-355386",
  phone3Tel: "+912626355386",
  whatsapp: "70169 75324",
  whatsappTel: "917016975324",
  upiId: "sahyo96249474@barodampay",
  email1: "sahyogpfir@gmail.com",
  email2: "info.nobleinfinity@gmail.com",
  web: "www.sahyogtapi.in",
};

export default function Contact() {
  const { t } = useI18n();
  const getRecaptchaToken = useRecaptchaToken();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "Personal Loan",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "Sending…" });
    try {
      // 0) Obtain reCAPTCHA v3 token (returns null if not configured)
      const recaptcha_token = await getRecaptchaToken("contact");
      // 1) Persist lead in our backend
      await Leads.create({ ...form, source: "landing-page-contact-form", recaptcha_token });
      // 2) Also open WhatsApp pre-filled for instant chat
      const text = `Hello Sahyog Finance,%0A%0AName: ${form.name}%0APhone: ${form.phone}%0AEmail: ${form.email}%0AInterested in: ${form.interest}%0A%0A${form.message}`;
      window.open(`https://wa.me/${CONTACT.whatsappTel}?text=${text}`, "_blank");
      setStatus({ state: "success", message: "Lead saved · Opening WhatsApp" });
      setForm({ name: "", phone: "", email: "", interest: "Personal Loan", message: "" });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 5000);
    } catch (err) {
      setStatus({
        state: "error",
        message:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Could not save right now — please call us directly.",
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#FAF6EC] text-[#0C1530] py-24 md:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#D4AF37]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase text-[#B68F1E]">
              Get in touch
            </span>
            <h2
              data-testid="contact-heading"
              className="font-display text-[40px] md:text-[60px] mt-5 leading-[1.05] tracking-tight"
            >
              Let&apos;s start a <em className="not-italic text-[#B68F1E]">conversation</em>
            </h2>
            <p className="mt-4 text-[#0C1530]/70 max-w-2xl mx-auto">
              Whether you need funding, advice or want to partner with us — our team is one call,
              click or chat away.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Contact form */}
          <Reveal className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-white rounded-3xl p-7 md:p-10 border border-[#0A1F44]/10 shadow-[0_20px_60px_-30px_rgba(10,31,68,0.35)]"
            >
              <h3 className="font-display text-2xl md:text-3xl">Send us a message</h3>
              <p className="mt-1 text-[#0C1530]/60 text-sm">
                Fill out the form — we&apos;ll route it to the right team and call you back in 24
                hours.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <input
                  data-testid="contact-input-name"
                  required
                  placeholder={t("contact.field.name")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
                />
                <input
                  data-testid="contact-input-phone"
                  required
                  type="tel"
                  placeholder={t("contact.field.phone")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
                />
                <input
                  data-testid="contact-input-email"
                  type="email"
                  placeholder={t("contact.field.email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
                />
                <select
                  data-testid="contact-input-interest"
                  aria-label="Product interest"
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  className="px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
                >
                  <option>Personal Loan</option>
                  <option>Business Loan</option>
                  <option>Agriculture Loan</option>
                  <option>Mortgage Loan</option>
                  <option>Housing Loan</option>
                  <option>Cattle Loan (Pashupalan)</option>
                  <option>Loan against FD</option>
                  <option>Loan against Credit Card</option>
                  <option>Social-Credit Loan</option>
                  <option>Project / Corporate Loan</option>
                  <option>Investment / Mutual Funds</option>
                  <option>Become a DSA / Associate</option>
                </select>
              </div>

              <textarea
                data-testid="contact-input-message"
                rows={5}
                placeholder={t("contact.field.message")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-4 w-full px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition resize-none"
              />

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={status.state === "loading"}
                className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-70"
              >
                {status.state === "success" ? (
                  <>
                    <CheckCircle2 size={16} /> {status.message}
                  </>
                ) : status.state === "error" ? (
                  <>
                    <AlertCircle size={16} /> {t("apply.step.back", "Try again")}
                  </>
                ) : status.state === "loading" ? (
                  <>Sending…</>
                ) : (
                  <>
                    {t("contact.submit")} <Send size={16} />
                  </>
                )}
              </button>
              {status.state === "error" && (
                <p
                  data-testid="contact-error"
                  className="mt-3 text-[12px] text-red-600 flex items-center gap-1.5"
                >
                  <AlertCircle size={12} /> {status.message}
                </p>
              )}
              <p className="mt-3 text-[12px] text-[#0C1530]/55">
                * Lead is saved with our team &amp; opens WhatsApp instantly for a quick chat.
              </p>
              {RECAPTCHA_ENABLED && (
                <p
                  data-testid="contact-recaptcha-notice"
                  className="mt-2 text-[11px] text-[#0C1530]/50 flex items-start gap-1.5 leading-snug"
                >
                  <ShieldCheck size={11} className="mt-0.5 text-[#B68F1E] shrink-0" />
                  <span>
                    Protected by reCAPTCHA — the Google{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-[#B68F1E]"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-[#B68F1E]"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </span>
                </p>
              )}
            </form>
          </Reveal>

          {/* Contact details */}
          <Reveal className="lg:col-span-5" delay={120}>
            <div className="bg-[#0A1F44] rounded-3xl p-7 md:p-10 text-white relative overflow-hidden noise-overlay h-full">
              <div className="pointer-events-none absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
              <h3 className="font-display text-2xl md:text-3xl">Reach us directly</h3>
              <p className="mt-1 text-white/65 text-sm">
                We&apos;re available Mon–Sat · 9:30 AM – 7:00 PM
              </p>

              <div className="mt-7 space-y-3.5">
                <a
                  data-testid="contact-detail-phone"
                  href={`tel:${CONTACT.phone1Tel}`}
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <Phone size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">
                      Mobile · Primary
                    </div>
                    <div className="font-display text-base text-white">{CONTACT.phone1}</div>
                  </div>
                </a>

                <a
                  data-testid="contact-detail-phone2"
                  href={`tel:${CONTACT.phone2Tel}`}
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <Phone size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">
                      Mobile · Secondary
                    </div>
                    <div className="font-display text-base text-white">{CONTACT.phone2}</div>
                  </div>
                </a>

                <a
                  data-testid="contact-detail-phone3"
                  href={`tel:${CONTACT.phone3Tel}`}
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <Phone size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">
                      Office Landline
                    </div>
                    <div className="font-display text-base text-white">{CONTACT.phone3}</div>
                  </div>
                </a>

                <a
                  data-testid="contact-detail-whatsapp"
                  href={`https://wa.me/${CONTACT.whatsappTel}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <MessageCircle size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">
                      WhatsApp
                    </div>
                    <div className="font-display text-base text-white">{CONTACT.whatsapp}</div>
                  </div>
                </a>

                <a
                  data-testid="contact-detail-email"
                  href={`mailto:${CONTACT.email1}`}
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <Mail size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">Email</div>
                    <div className="font-display text-base text-white">{CONTACT.email1}</div>
                    <div className="text-white/55 text-xs">{CONTACT.email2}</div>
                  </div>
                </a>

                <a
                  data-testid="contact-detail-web"
                  href={`https://${CONTACT.web}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 hover:bg-white/[0.04] transition group"
                >
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] group-hover:bg-[#D4AF37]/25">
                    <Globe size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">Website</div>
                    <div className="font-display text-base text-white">{CONTACT.web}</div>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-3.5 rounded-xl border border-[#D4AF37]/25">
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37] shrink-0">
                    <MapPin size={17} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">
                      Registered Office
                    </div>
                    <div className="text-white text-sm leading-snug mt-1">
                      A-106, Ambaji Complex,
                      <br />
                      Ambaji Mata Road, Vyara,
                      <br />
                      Tapi, Gujarat — 394650
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
